import { useEffect, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Loader2, Save, X, User as UserIcon, Trophy, BookOpen, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  'tactical', 'player_feedback', 'fitness', 'behaviour',
  'parent', 'match_reflection', 'training_idea', 'admin', 'general',
];

const DEST_META = {
  player_profile: { icon: UserIcon, label: 'Player Profile', color: 'blue' },
  match_notes: { icon: Trophy, label: 'Match Notes', color: 'emerald' },
  journal: { icon: BookOpen, label: 'Coach Journal', color: 'amber' },
};

export default function VoiceJournalConfirmModal({ payload, context, onClose }) {
  const [open, setOpen] = useState(true);
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState(payload.suggested_title || '');
  const [body, setBody] = useState(payload.transcript || '');
  const [destination, setDestination] = useState(payload.suggested_destination || 'journal');
  const [playerId, setPlayerId] = useState(payload.suggested_player_id || context.context_player_id || '');
  const [matchId, setMatchId] = useState(payload.suggested_match_id || context.context_match_id || '');
  const [categories, setCategories] = useState(payload.categories || ['general']);
  const [players, setPlayers] = useState([]);
  const [matches, setMatches] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [pRes, mRes] = await Promise.all([
          axios.get(`${API}/players`),
          axios.get(`${API}/matches`),
        ]);
        setPlayers(pRes.data || []);
        // Show most recent 30 matches
        const mList = (mRes.data || []).slice().sort((a, b) => (b.date || '').localeCompare(a.date || '')).slice(0, 30);
        setMatches(mList);
      } catch (e) { /* ignore */ }
    })();
  }, []);

  const toggleCategory = (c) => {
    setCategories(prev => prev.includes(c) ? prev.filter(x => x !== c) : [...prev, c]);
  };

  const close = () => {
    setOpen(false);
    setTimeout(() => onClose?.(), 200);
  };

  const handleSave = async () => {
    if (!body.trim()) {
      toast.error('Add some text before saving');
      return;
    }
    if (destination === 'player_profile' && !playerId) {
      toast.error('Pick a player or change destination');
      return;
    }
    if (destination === 'match_notes' && !matchId) {
      toast.error('Pick a match or change destination');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/voice-journal/save`, {
        destination,
        transcript: payload.transcript,
        title: title.trim() || null,
        body: body.trim(),
        categories,
        player_id: destination === 'player_profile' ? playerId : null,
        match_id: destination === 'match_notes' ? matchId : null,
      });
      const meta = DEST_META[destination];
      toast.success(`Saved to ${meta.label}`);
      close();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); }}>
      <DialogContent className="max-w-2xl" data-testid="voice-journal-confirm-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-emerald-600" />
            Review &amp; save voice note
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Destination picker */}
          <div>
            <Label className="text-xs">Save to</Label>
            <div className="grid grid-cols-3 gap-2 mt-1" data-testid="voice-journal-destination-picker">
              {Object.entries(DEST_META).map(([key, meta]) => {
                const Icon = meta.icon;
                const active = destination === key;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setDestination(key)}
                    className={`p-3 rounded-lg border text-left transition-all ${
                      active
                        ? `bg-${meta.color}-50 border-${meta.color}-500 text-${meta.color}-800`
                        : 'bg-white border-gray-200 text-gray-700 hover:border-gray-300'
                    }`}
                    data-testid={`voice-journal-dest-${key}`}
                  >
                    <Icon className="w-4 h-4 mb-1" />
                    <p className="text-xs font-medium">{meta.label}</p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Context picker */}
          {destination === 'player_profile' && (
            <div>
              <Label className="text-xs">Player</Label>
              <Select value={playerId} onValueChange={setPlayerId}>
                <SelectTrigger data-testid="voice-journal-player-select">
                  <SelectValue placeholder="Select player..." />
                </SelectTrigger>
                <SelectContent>
                  {players.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
              {payload.mentioned_players?.length > 0 && (
                <p className="text-xs text-gray-500 mt-1">
                  AI heard: {payload.mentioned_players.map(m => `${m.name} (${m.confidence})`).join(', ')}
                </p>
              )}
            </div>
          )}

          {destination === 'match_notes' && (
            <div>
              <Label className="text-xs">Match</Label>
              <Select value={matchId} onValueChange={setMatchId}>
                <SelectTrigger data-testid="voice-journal-match-select">
                  <SelectValue placeholder="Select match..." />
                </SelectTrigger>
                <SelectContent>
                  {matches.map(m => (
                    <SelectItem key={m.id} value={m.id}>
                      {m.opponent_name || 'Match'} — {m.date}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Title */}
          <div>
            <Label className="text-xs">Title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} data-testid="voice-journal-title-input" />
          </div>

          {/* Body (editable transcript) */}
          <div>
            <Label className="text-xs">Note</Label>
            <Textarea
              rows={5}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              className="resize-none"
              data-testid="voice-journal-body-input"
            />
            <p className="text-xs text-gray-400 mt-1">{payload.summary}</p>
          </div>

          {/* Categories */}
          <div>
            <Label className="text-xs">Categories</Label>
            <div className="flex flex-wrap gap-2 mt-1" data-testid="voice-journal-categories">
              {CATEGORIES.map(c => {
                const active = categories.includes(c);
                return (
                  <button
                    key={c}
                    type="button"
                    onClick={() => toggleCategory(c)}
                    className={`px-2.5 py-1 rounded-full text-xs border transition-all ${
                      active
                        ? 'bg-emerald-500 text-white border-emerald-500'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-emerald-400'
                    }`}
                  >
                    {c.replace('_', ' ')}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={close} disabled={saving} data-testid="voice-journal-discard-btn">
            <X className="w-4 h-4 mr-1" />Discard
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="voice-journal-save-btn"
          >
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : <><Save className="w-4 h-4 mr-2" />Save Note</>}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
